import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { Button } from '@/components/UI/Button';

const focus = css`
  &:focus-visible { outline: 3px solid #7f9de8; outline-offset: 3px; }
  touch-action: manipulation;
`;
export const Surface = styled.form`
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  display: flex; flex-direction: column;
  width: min(800px, calc(100vw - 32px)); max-height: min(850px, calc(100vh - 40px));
  max-height: min(850px, calc(100dvh - 40px));
  background: #fff; color: ${({ theme }) => theme.colors.gray500};
  border-radius: 40px 40px 40px 0; overflow: hidden;
  font-size: ${({ theme }) => theme.fontSizes.base}; line-height: 1.55;
  &:focus-visible { outline: 3px solid #7f9de8; outline-offset: 3px; }
  @media (max-width: 600px) { border-radius: 28px 28px 28px 0; }
`;
export const Header = styled.header`
  display: flex; align-items: flex-start; gap: 24px; padding: 32px 36px 24px;
  border-bottom: 1px solid #e7ebf2;
  @media (max-width: 600px) { padding: 24px 22px 20px; gap: 12px; }
`;
export const Heading = styled.h2`
  font-size: 28px; font-weight: 700; line-height: 1.25; letter-spacing: -.02em;
  color: ${({ theme }) => theme.colors.primary500}; margin: 0 0 12px; text-wrap: balance;
  @media (max-width: 600px) { font-size: 24px; }
`;
export const Description = styled.p`
  margin: 0; color: #586477; font-size: ${({ theme }) => theme.fontSizes.base}; line-height: 1.65;
`;
export const CloseButton = styled(Button)`
  ${focus}; flex: 0 0 44px; width: 44px; height: 44px; padding: 12px;
  border: 1px solid #e0e5ee; border-radius: 50%; color: #42516b; background: #fff;
  text-decoration: none; min-width: 0;
  svg { width: 18px; height: 18px; }
  &:hover { background: #eef1f8; color: #0b319d; }
`;
export const ScrollBody = styled.div`
  min-height: 0; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable;
  padding: 28px 36px; scroll-padding: 20px;
  @media (max-width: 600px) { padding: 24px 22px; }
`;
export const Target = styled.div`
  display: flex; align-items: center; gap: 14px; min-width: 0;
  padding: 16px 18px; margin: 0 0 26px; background: #f5f7fb; border-radius: 10px;
  strong { display: block; font-weight: 600; overflow-wrap: anywhere; line-height: 1.5; }
  small { display: block; color: #657187; font-size: 15px; margin-bottom: 2px; }
`;
export const FileIcon = styled.span`
  flex: 0 0 42px; display: grid; place-items: center; width: 42px; height: 46px;
  background: #fff; border: 1px solid #e0e6f0; border-radius: 8px; color: #0b319d;
  svg { width: 24px; height: 24px; }
`;
export const ComposeRow = styled.div`
  display: flex; align-items: flex-start; gap: 12px;
  .MuiFormControl-root { flex: 1; min-width: 0; }
  .MuiInputBase-root { min-height: 52px; border-radius: 7px; font-size: 18px; }
  .MuiInputLabel-root { font-size: 18px; }
  .MuiOutlinedInput-notchedOutline { border-color: #bac4d4; }
  .Mui-focused .MuiOutlinedInput-notchedOutline { border-color: #0b319d !important; }
  @media (max-width: 600px) { flex-direction: column; > button { width: 100%; } }
`;
export const PrimaryButton = styled(Button).attrs({ variant: 'primary' })`
  ${focus}; min-height: 52px; height: auto; padding: 12px 24px; border-radius: 7px;
  font-size: 18px; line-height: 1.4; white-space: nowrap;
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  .MuiCircularProgress-root { color: currentColor; }
  @media (prefers-reduced-motion: reduce) { .MuiCircularProgress-root, .MuiCircularProgress-circle { animation: none; } }
`;
export const QuietButton = styled(Button).attrs({ variant: 'primary-invert' })`
  ${focus}; min-height: 44px; height: auto; padding: 8px 12px; border: 0; background: transparent;
  font-size: 16px; font-weight: 600; color: #0b319d; text-decoration: none;
  &:hover { background: #eef1f8; color: #082574; }
`;
export const DangerButton = styled(QuietButton)`
  color: #ae2738;
  &:hover { background: #fff0f2; color: #8d1525; }
`;
export const Hint = styled.p`
  font-size: 15px; line-height: 1.6; color: #657187; margin: 12px 0 0;
`;
export const Feedback = styled.p<{ $error?: boolean }>`
  margin: 16px 0 0; padding: 12px 16px; font-size: 16px; line-height: 1.6;
  border-left: 3px solid ${({ $error }) => $error ? '#ae2738' : '#4c7f32'};
  background: ${({ $error }) => $error ? '#fff4f5' : '#f3f8f0'};
  color: ${({ $error }) => $error ? '#8d1525' : '#355827'};
`;
export const ListSection = styled.section`margin-top: 32px;`;
export const SectionHeading = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px;
  h3 { font-size: 19px; font-weight: 700; margin: 0; }
`;
export const InvitationList = styled.ul`list-style: none; margin: 0; padding: 0;`;
export const InvitationRow = styled.li`
  border-bottom: 1px solid #e7ebf2; padding: 20px 0;
  &:last-child { border-bottom: 0; padding-bottom: 0; }
`;
export const RowLayout = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 16px; align-items: start;
  @media (max-width: 600px) { grid-template-columns: minmax(0, 1fr); gap: 10px; }
`;
export const Recipient = styled.p`font-weight: 600; font-size: 18px; overflow-wrap: anywhere; margin: 0 0 6px;`;
export const RowMeta = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px 14px;
  color: #657187; font-size: 15px; line-height: 1.6;
  time { font-variant-numeric: tabular-nums; }
`;
export const Status = styled.span<{ $status: string }>`
  display: inline-flex; gap: 7px; align-items: center; font-size: 15px; font-weight: 600;
  color: ${({ $status }) => $status === 'accepted' ? '#3f6e2b' : $status === 'pending' ? '#865806' : '#626d7b'};
  &::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
`;
export const RowActions = styled.div`display: flex; align-items: center; flex-wrap: wrap; gap: 2px; margin: -6px -8px 0 0;`;
export const EmptyState = styled.div`
  padding: 28px 16px; text-align: center; border: 1px dashed #d6deea; border-radius: 10px;
  color: #586477; margin-top: 12px; font-size: 17px; line-height: 1.6;
  p { margin: 0; } small { display: block; margin-top: 6px; font-size: 15px; }
`;
export const ConfirmArea = styled.div`
  background: #fff7f7; padding: 14px 16px; border-radius: 7px; margin-top: 14px; color: #8d1525;
  p { font-size: 16px; line-height: 1.6; margin: 0 0 8px; }
`;
export const Footer = styled.footer`
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
  padding: 18px 36px; border-top: 1px solid #e7ebf2; background: #fff;
  p { margin: 0; font-size: 15px; line-height: 1.6; color: #657187; }
  @media (max-width: 600px) { padding: 16px 22px; gap: 12px; }
`;
export const SecondaryButton = styled(Button).attrs({ variant: 'primary-invert' })`
  ${focus}; min-height: 44px; height: auto; padding: 10px 24px; border-radius: 7px; font-size: 17px;
`;
export const Pagination = styled.nav`
  display: flex; align-items: center; justify-content: center; gap: 16px; padding-top: 20px;
  font-size: 16px; font-variant-numeric: tabular-nums;
`;
export const InvitationPage = styled.main`
  width: min(720px, calc(100% - 40px)); margin: 64px auto; color: #212121; line-height: 1.6;
  @media (max-width: 600px) { width: calc(100% - 32px); margin: 32px auto; }
`;
export const InvitationPanel = styled.section`
  border: 1px solid #e0e6ef; border-radius: 32px 32px 32px 0; background: #fff; padding: 40px;
  @media (max-width: 600px) { padding: 28px 22px; border-radius: 24px 24px 24px 0; }
`;
export const PageActions = styled.div`
  display: flex; flex-wrap: wrap; gap: 12px; margin-top: 28px;
  @media (max-width: 600px) { > a, > button { width: 100%; } }
`;
export const ActionLink = styled(Link)<{ $secondary?: boolean }>`
  ${focus}; display: inline-flex; align-items: center; justify-content: center;
  min-height: 50px; padding: 10px 24px; border-radius: 7px; font-size: 18px; font-weight: 600;
  background: ${({ $secondary }) => $secondary ? '#fff' : '#0b319d'};
  color: ${({ $secondary }) => $secondary ? '#0b319d' : '#fff'}; border: 1px solid #0b319d;
  text-decoration: none;
  &:hover { background: ${({ $secondary }) => $secondary ? '#eef1f8' : '#082574'}; }
`;
export const DetailList = styled.dl`
  margin: 24px 0 0; border-top: 1px solid #e7ebf2; padding-top: 20px;
  > div { display: flex; justify-content: space-between; gap: 16px; padding: 6px 0; }
  dt { color: #657187; font-size: 16px; } dd { margin: 0; font-size: 16px; text-align: right; }
  time { font-variant-numeric: tabular-nums; }
`;
export const ResumeNotice = styled.div`
  padding: 14px 24px; background: #eef1f8; font-size: 17px;
  a { ${focus}; color: #0b319d; text-decoration: underline; text-underline-offset: 3px; }
`;
// External (account-free) sharing: access mode choice, the limits list and the code entry row.
export const ModeGroup = styled.fieldset`
  border: 0; margin: 24px 0 0; padding: 0; min-width: 0;
  legend { font-size: 16px; font-weight: 600; color: #1d2a44; margin-bottom: 8px; padding: 0; }
`;
export const ModeOption = styled.label<{ $disabled?: boolean }>`
  display: flex; gap: 12px; align-items: flex-start; padding: 12px 14px; margin-top: 8px; min-height: 44px;
  border: 1px solid #d6deea; border-radius: 8px; cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  &:has(input:checked) { border-color: #0b319d; background: #f4f7fd; }
  input { margin: 4px 0 0; width: 18px; height: 18px; accent-color: #0b319d; flex-shrink: 0; ${focus}; }
  strong { display: block; font-size: 16px; color: #1d2a44; }
  span { display: block; font-size: 15px; line-height: 1.55; color: #586477; margin-top: 2px; }
`;
export const LimitList = styled.ul`
  margin: 12px 0 0; padding: 12px 16px 12px 34px; border-radius: 8px; background: #f6f8fc;
  font-size: 15px; line-height: 1.6; color: #34405a;
  li + li { margin-top: 4px; }
`;
export const CodeRow = styled.div`
  display: flex; gap: 12px; align-items: flex-start; margin-top: 20px;
  @media (max-width: 600px) { flex-direction: column; align-items: stretch; }
`;